/**
 * The properties column — the Linear / Notion way (design: "Led AI Console
 * Redesign", turn 13).
 *
 * > No expanding panels, no Cancel/Save, no explanatory notes. Properties are
 * > a quiet glyph → value column (Linear's panel, 2026-09-20 — the glyph is
 * > the label; see "Glyphs" below); the value is the control. Click text and
 * > type. Fixed choices and directories use a searchable combobox. The current
 * > value is checked. Everything saves on blur and
 * > syncs to Procore silently — the green dot in the header is the only
 * > confirmation. Single-letter shortcuts (S, A, D, P, L) open each picker
 * > from anywhere on the page.
 *
 * ## It renders the SAME fields Review does, in a different shape
 *
 * `fieldsFor` and `buildReviewPatch` are imported from `ReviewQueue`, not
 * reimplemented. This is the one rule the console has never bent: the phone,
 * the review queue and this page must agree about what a record is made of
 * and what a patch to it looks like. A second field vocabulary here would be
 * a second thing to keep in step with the mobile schema, and it would drift —
 * the last time two surfaces each decided what a task was made of, they
 * disagreed within a week.
 *
 * So turn 13 changes PRESENTATION only. Which properties a record has, what
 * they are called, which directory backs them and what key they patch under
 * all still come from one place.
 *
 * ## The control is chosen by the field's own shape
 *
 * | the field has… | the control (design) |
 * |---|---|
 * | `choices` | a searchable combobox |
 * | `reference` | a searchable directory combobox |
 * | `inputType: "date"` | a natural-language line over a month grid (13d) |
 * | nothing | the value itself, as an input — click and type (13a) |
 *
 * That mapping is why this component needs no per-kind list of its own. A
 * task's Status arrives with five `choices` and gets a searchable list; a punch's
 * Location arrives with `reference: "location"` and gets the directory. Add a
 * field to the mobile schema and it appears here wearing the right control.
 *
 * ## The multiline field is not a property
 *
 * A record's description is its own words, not a fact about it.
 * `PropertyEditor` gives it its own area above or beside the properties,
 * depending on the card width. `PropertyRows` skips it.
 *
 * ## Shortcuts are first letters, and they are not invented
 *
 * The design names S, A, D, P, L — Status, Assignee, Due, Priority, Location.
 * Those are first letters, so this derives them rather than mapping them: the
 * first property whose label starts with a letter claims it, later ones go
 * without. A hand-written map would be a fifth place a field name has to be
 * spelled, and the one that silently stops matching.
 *
 * A shortcut fires only when focus is not already in a text control —
 * otherwise typing "so the drywall…" into a description would open the status
 * picker on the "s".
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { ConsoleRow, ItemEditDirectoryOption, ItemEditOptions } from "../api";
import { fetchItemEditOptions, itemEditCategory } from "../api";
import { updateDirectoryFields } from "../lib/assigneeCompanyTrade";
import {
  buildReviewPatch,
  choiceLabel,
  fieldsFor,
  type ReviewField,
} from "../views/ReviewQueue";
import {
  mobileRecordDefinition,
  mobileRecordKindLabel,
} from "../mobileRecordPresentation";
import { AnchoredPanel } from "./AnchoredPanel";
import { AssigneePicker } from "./AssigneePicker";
import { C, FONT_MONO } from "../tokens";
import { KIND_LABEL, MONTHS, WEEKDAYS_SHORT } from "./chrome";
import { Calendar, isoDay, parseDay } from "./Calendar";
import { NO_AUTOFILL } from "../lib/noAutofill";
import { FieldCombobox } from "./FieldCombobox";

// ---------------------------------------------------------------------------
// The picker's chrome — 13b–13f draw one popover, five times
// ---------------------------------------------------------------------------

/** Turn 13's picker: a lighter edge than a Review dropdown, and the
 *  redesign's two-layer shadow rather than one hard one. */
const PICKER_STYLE: React.CSSProperties = {
  padding: 6,
  border: `1px solid ${C.popLine}`,
  borderRadius: 12,
  boxShadow: `0 1px 2px ${C.hairRow}, 0 14px 36px ${C.popShadow}`,
};

/**
 * Focus the picker's filter as soon as it can hold focus.
 *
 * Not simply `useEffect(() => ref.current?.focus(), [])`. `AnchoredPanel`
 * keeps the panel `visibility: hidden` until its layout effect has measured
 * where to put it — so it cannot flash at 0,0 — and a hidden element is not
 * focusable. The first attempt therefore lands on nothing and the reader has
 * to click the line before they can type into it, which is the one thing
 * "click a pick and a popover opens with a type-to-filter line" must not
 * require. So it retries on the next frame, a few times, and gives up.
 */
function useFilterFocus(ref: React.RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    let frame = 0;
    let tries = 0;
    const attempt = () => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      if (document.activeElement === node || tries++ > 8) return;
      frame = requestAnimationFrame(attempt);
    };
    attempt();
    return () => cancelAnimationFrame(frame);
  }, [ref]);
}

/** The filter line at the top of a picker, and the key cap that names its
 *  shortcut. */
function PickerHeader({
  placeholder,
  shortcut,
  query,
  onQuery,
  inputRef,
  onKeyDown,
}: {
  placeholder: string;
  shortcut?: string;
  query: string;
  onQuery: (value: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 34,
        padding: "0 8px",
        borderBottom: `1px solid ${C.railEdge}`,
        marginBottom: 4,
      }}
    >
      <input
        ref={inputRef}
        data-testid="picker-filter"
        // Every property-row picker's filter box comes through here — the
        // location tree, the directory, the enums. None of them is a field
        // the browser can know anything about, and Chrome laying its address
        // dropdown over the options underneath is the failure this prevents.
        // See `lib/noAutofill`.
        {...NO_AUTOFILL}
        aria-label={placeholder}
        placeholder={placeholder}
        value={query}
        // BOTH, as `TimeField` carries both and for the same reason: React's
        // synthetic `change` is driven by its own value tracker, which does
        // not fire under the test DOM, and `input` is the event a keystroke
        // actually produces. They are idempotent — each sets the same string.
        onInput={(event) => onQuery((event.target as HTMLInputElement).value)}
        onChange={(event) => onQuery(event.target.value)}
        onKeyDown={onKeyDown}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          background: "transparent",
          padding: 0,
          fontFamily: "inherit",
          fontSize: 15.52,
          color: C.ink,
          outline: "none",
        }}
      />
      {shortcut && (
        <span
          aria-hidden
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11.5,
            color: C.propLabel,
            padding: "2px 6px",
            borderRadius: 5,
            background: C.hairRow,
          }}
        >
          {shortcut}
        </span>
      )}
    </div>
  );
}

/** One row in a picker. `number` is the key that selects it, `detail` the
 *  quiet second line (a company, a location path, "nudged weekly"). */
function PickerOption({
  label,
  detail,
  dot,
  glyph,
  checked,
  number,
  onSelect,
}: {
  label: string;
  detail?: string;
  dot?: string;
  glyph?: React.ReactNode;
  checked: boolean;
  number?: number;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={checked}
      data-testid="picker-option"
      data-checked={checked ? "true" : "false"}
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        height: 34,
        padding: "0 8px",
        borderRadius: 8,
        border: "none",
        width: "100%",
        textAlign: "left",
        background: checked ? C.hairRow : "transparent",
        cursor: "pointer",
        font: "inherit",
        color: C.ink,
      }}
    >
      {dot && (
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: 4, background: dot, flex: "none" }}
        />
      )}
      {glyph}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "baseline",
          gap: 6,
        }}
      >
        <span style={{ fontSize: 15.52, fontWeight: 500, whiteSpace: "nowrap" }}>
          {label}
        </span>
        {detail && (
          <span
            style={{
              fontSize: 13.23,
              color: C.propLabel,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {detail}
          </span>
        )}
      </span>
      {checked && <CheckGlyph />}
      {number !== undefined && (
        <span
          aria-hidden
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11.5,
            color: C.popNum,
            width: 10,
            textAlign: "right",
          }}
        >
          {number}
        </span>
      )}
    </button>
  );
}

function CheckGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M2.5 7.5l3 3 6-7"
        stroke={C.railText}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Linear's due-date calendar, in its three moods (user-directed, 2026-09-20):
 * grey with a filled header band when the date is a week or more out, orange
 * when it falls inside the week, red with an × badge cut into the corner once
 * it has passed. The mood lives in the GLYPH, not the words beside it — the
 * date itself stays in the value ink whichever way it leans.
 */
function CalendarGlyph({ due }: { due: DueTone | "none" }) {
  const tone = due === "overdue" ? C.red : due === "soon" ? C.orange : C.railGlyph;
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      <rect x="2" y="3" width="12" height="11" rx="2.5" stroke={tone} strokeWidth="1.5" />
      <path d="M2 5.5a2.5 2.5 0 012.5-2.5h7A2.5 2.5 0 0114 5.5v1.3H2z" fill={tone} />
      {due === "overdue" && (
        <>
          <circle cx="12.6" cy="12.6" r="4.6" fill={C.surface} />
          <circle cx="12.6" cy="12.6" r="3.4" fill={tone} />
          <path d="M11.2 11.2l2.8 2.8M14 11.2l-2.8 2.8" stroke={C.surface} strokeWidth="1.3" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M7 12.6s4.2-4 4.2-7A4.2 4.2 0 007 1.4a4.2 4.2 0 00-4.2 4.2c0 3 4.2 7 4.2 7z"
        stroke={C.railGlyph}
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="5.5" r="1.5" stroke={C.railGlyph} strokeWidth="1.3" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Dots
// ---------------------------------------------------------------------------

/**
 * A choice's dot, by the RAW stored value.
 *
 * Keyed on the mobile vocabularies rather than on the console's derived
 * `status`, because that is what a `choices` field actually holds. Anything
 * unknown gets the neutral dot instead of no dot: a picker where some rows
 * have a dot and some do not reads as a rendering fault, and a new status
 * added upstream must not be the row that looks broken.
 */
const CHOICE_DOT: Record<string, string> = {
  // Task workflow
  not_started: C.propNeutralDot,
  initiated: C.amber,
  in_progress: C.blue,
  ready_for_review: C.green,
  completed: C.railText,
  voided: C.propNeutralDot,
  // Punch / observation priority (13e: three options, one keypress)
  high: C.red,
  medium: C.amber,
  low: C.propNeutralDot,
  // Observation status
  open: C.amber,
  closed: C.railText,
};

function dotFor(value: string): string {
  return CHOICE_DOT[value] ?? C.propNeutralDot;
}

// ---------------------------------------------------------------------------
// Glyphs — the Linear property panel, copied as closely as the palette allows
// ---------------------------------------------------------------------------

/**
 * Linear's sidebar has no label column. Each property leads with a glyph
 * that IS the label — a progress ring for status, three bars for priority,
 * the assignee's own avatar, a calendar for the due date — and the word
 * beside it is the value (user-directed, 2026-09-20). The label survives for
 * assistive tech and for the tests as a visually-hidden span, and as the
 * glyph's tooltip, which is also Linear's answer to "what is this row".
 *
 * The geometry is Linear's own: a 14px ring, `r=6`, stroke 1.5; the progress
 * pie is a second circle of `r=2` with a 4px stroke, dashed to the fraction
 * done and rotated so it starts at twelve o'clock. Done is the ring filled
 * with a check inside. Priority is three bottom-aligned bars in a 16px box,
 * the unfilled ones at 0.35 opacity.
 */

/** How far along a workflow status is, as the ring draws it. `null` is
 *  "done": the ring fills and takes a check. */
const STATUS_RING: Record<string, { fraction: number | null; color: string }> = {
  // Task workflow
  not_started: { fraction: 0, color: C.propNeutralDot },
  initiated: { fraction: 0.25, color: C.amber },
  in_progress: { fraction: 0.5, color: C.blue },
  ready_for_review: { fraction: 0.75, color: C.green },
  completed: { fraction: null, color: C.railText },
  voided: { fraction: 0, color: C.propNeutralDot },
  // Observation status
  open: { fraction: 0, color: C.amber },
  closed: { fraction: null, color: C.railText },
};

function StatusRing({ value }: { value: string }) {
  const ring = STATUS_RING[value] ?? { fraction: 0, color: dotFor(value) };
  // Circumference of the r=2 pie circle: 2π·2 ≈ 12.566.
  const dash = ring.fraction === null ? 0 : Math.max(0, Math.min(1, ring.fraction)) * 12.566;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flexShrink: 0 }}>
      {ring.fraction === null ? (
        <>
          <circle cx="7" cy="7" r="6.5" fill={ring.color} />
          <path d="M4.2 7.2l1.9 1.9 3.8-4" stroke={C.surface} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <circle cx="7" cy="7" r="6" stroke={ring.color} strokeWidth="1.5" strokeDasharray={value === "not_started" || value === "voided" ? "1.4 1.74" : undefined} />
          {dash > 0 && (
            <circle cx="7" cy="7" r="2" stroke={ring.color} strokeWidth="4" strokeDasharray={`${dash} 100`} transform="rotate(-90 7 7)" />
          )}
        </>
      )}
    </svg>
  );
}

const PRIORITY_BARS: Record<string, number> = { low: 1, medium: 2, high: 3 };

function PriorityBars({ value }: { value: string }) {
  const filled = PRIORITY_BARS[value] ?? 0;
  const bars = [
    { x: 1.5, y: 8, h: 6 },
    { x: 6.5, y: 5, h: 9 },
    { x: 11.5, y: 2, h: 12 },
  ];
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden style={{ flexShrink: 0 }}>
      {bars.map((bar, index) => (
        <rect key={bar.x} x={bar.x} y={bar.y} width="3" height={bar.h} rx="1"
          fill={C.railText} fillOpacity={index < filled ? 1 : 0.28} />
      ))}
    </svg>
  );
}

const GLYPH_STROKE: React.SVGAttributes<SVGElement> = {
  stroke: C.railGlyph, strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round", fill: "none",
};

/** Linear's label tag. Stands for the trade — the label a record wears. */
function TagGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden style={{ flexShrink: 0 }} {...GLYPH_STROKE}>
      <path d="M2.5 2.5h5.2l5.8 5.8a1.2 1.2 0 010 1.7l-3.5 3.5a1.2 1.2 0 01-1.7 0L2.5 7.7z" />
      <circle cx="5.6" cy="5.6" r="1" fill={C.railGlyph} stroke="none" />
    </svg>
  );
}

function BuildingGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden style={{ flexShrink: 0 }} {...GLYPH_STROKE}>
      <rect x="3" y="2.5" width="10" height="11" rx="1.2" />
      <path d="M6 5.5h1.2M8.8 5.5H10M6 8h1.2M8.8 8H10M6.8 13.5V11h2.4v2.5" />
    </svg>
  );
}

/** Linear's "unassigned": a dashed ring around a person. */
function PersonGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden style={{ flexShrink: 0 }} {...GLYPH_STROKE}>
      <circle cx="8" cy="8" r="6.75" strokeDasharray="2 1.8" />
      <circle cx="8" cy="6.4" r="1.9" />
      <path d="M4.6 12.2c.7-1.6 1.9-2.4 3.4-2.4s2.7.8 3.4 2.4" />
    </svg>
  );
}

function LinesGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden style={{ flexShrink: 0 }} {...GLYPH_STROKE}>
      <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h7" />
    </svg>
  );
}

/** The assignee's avatar with Linear's small presence dot, bottom-right — here
 *  it says "somebody holds this", against the dashed ring that says nobody
 *  does yet. */
function AvatarLead({ name }: { name: string }) {
  return (
    <span aria-hidden style={{ position: "relative", width: 20, height: 20, flex: "none", display: "grid" }}>
      <Initials name={name} />
      <span style={{ position: "absolute", right: -2, bottom: -2, width: 8, height: 8, borderRadius: 4, background: C.green, boxShadow: `0 0 0 2px ${C.surface}` }} />
    </span>
  );
}

/** The glyph a field leads with, by what the field is. */
function glyphFor(field: ReviewField, value: string, today: Date): React.ReactNode {
  if (field.key === "status" || field.key === "workflowStatus") return <StatusRing value={value} />;
  if (field.key === "priority" || field.key === "severity") return <PriorityBars value={value} />;
  if (field.inputType === "date") {
    return <CalendarGlyph due={value ? dueLabel(value, today).tone : "none"} />;
  }
  if (field.reference === "assignee") return value ? <AvatarLead name={value} /> : <PersonGlyph />;
  if (field.reference === "location") return <PinGlyph />;
  if (field.reference === "trade") return <TagGlyph />;
  if (field.reference === "company") return <BuildingGlyph />;
  if (field.choices) return <StatusRing value={value} />;
  return <LinesGlyph />;
}

/** The glyph an OPTION wears in a picker — the same one its value would lead
 *  the row with, so choosing "In Progress" from the list and reading it in the
 *  row look the same. */
function optionGlyphFor(field: ReviewField, choice: string): React.ReactNode {
  if (field.key === "priority" || field.key === "severity") return <PriorityBars value={choice} />;
  if (field.choices) return <StatusRing value={choice} />;
  return <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: dotFor(choice) }} />;
}

function referenceGlyphFor(reference: ReviewField["reference"], name: string): React.ReactNode {
  if (reference === "location") return <PinGlyph />;
  if (reference === "trade") return <TagGlyph />;
  if (reference === "company") return <BuildingGlyph />;
  return <Initials name={name} />;
}

/** The row's lead: the glyph, with the label as its tooltip and as text only
 *  a screen reader (or a test) sees. An empty value dims it — Linear's rows
 *  keep their icons when nothing is set, so the eye still finds the row. */
function PropertyLead({
  field,
  value,
  today,
  onClick,
  hideLabel = false,
}: {
  field: ReviewField;
  value: string;
  today: Date;
  /** The date row's lead sits inside the value's pill rather than in the
   *  row's glyph column, so a click on it should open the picker too. */
  onClick?: () => void;
  hideLabel?: boolean;
}) {
  const tone = field.inputType === "date" && value ? dueLabel(value, today).tone : undefined;
  return (
    <span
      data-testid="property-glyph"
      className="property-glyph"
      data-tone={tone}
      title={field.label}
      onClick={onClick}
      style={{ width: 20, height: 20, display: "grid", placeItems: "center", flex: "none", opacity: value ? 1 : 0.55 }}
    >
      {glyphFor(field, value, today)}
      {!hideLabel && <span className="sr-only">{field.label}</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/* `isoDay` and `parseDay` live in `Calendar` now — one calendar, one set of
   date helpers. Re-exported because this module's tests already import them
   from here. */
export { isoDay, parseDay } from "./Calendar";

/**
 * 13d's natural-language line: "Sep 15, next Mon, +2w…".
 *
 * Deliberately a small, closed set rather than a date-parsing library. Every
 * form here is one somebody would type on purpose; anything else returns
 * `null` and the grid below is how they answer instead. A parser that GUESSED
 * would eventually put a due date on the wrong day, which is the one outcome
 * a due date cannot survive.
 */
export function parseDateInput(raw: string, today: Date): string | null {
  const text = raw.trim().toLowerCase();
  if (!text) return null;

  // Already a date.
  const exact = parseDay(text);
  if (exact) return isoDay(exact);

  // today / tomorrow / yesterday
  if (text === "today") return isoDay(today);
  if (text === "tomorrow" || text === "tmw") return isoDay(shift(today, 1));
  if (text === "yesterday") return isoDay(shift(today, -1));

  // +2w, +3d, +1m — and the bare "2w" form.
  const relative = /^\+?(\d{1,3})\s*(d|day|days|w|week|weeks|m|month|months)$/.exec(text);
  if (relative) {
    const amount = Number(relative[1]);
    const unit = relative[2]![0];
    if (unit === "d") return isoDay(shift(today, amount));
    if (unit === "w") return isoDay(shift(today, amount * 7));
    return isoDay(
      new Date(today.getFullYear(), today.getMonth() + amount, today.getDate()),
    );
  }

  // next mon / mon — the coming weekday, never today itself.
  const weekday = /^(next\s+)?(sun|mon|tue|wed|thu|fri|sat)[a-z]*$/.exec(text);
  if (weekday) {
    const target = WEEKDAYS_SHORT.findIndex(
      (day) => day.toLowerCase() === weekday[2],
    );
    if (target >= 0) {
      const ahead = (target - today.getDay() + 7) % 7 || 7;
      return isoDay(shift(today, ahead));
    }
  }

  // "Sep 15" / "15 Sep" / "Sep 15 2027"
  const monthDay = /^([a-z]{3,})\s+(\d{1,2})(?:,?\s*(\d{4}))?$/.exec(text);
  const dayMonth = /^(\d{1,2})\s+([a-z]{3,})(?:,?\s*(\d{4}))?$/.exec(text);
  const named = monthDay
    ? { month: monthDay[1]!, day: monthDay[2]!, year: monthDay[3] }
    : dayMonth
      ? { month: dayMonth[2]!, day: dayMonth[1]!, year: dayMonth[3] }
      : null;
  if (named) {
    const index = MONTHS.findIndex(
      (month) => month.toLowerCase() === named.month.slice(0, 3),
    );
    const day = Number(named.day);
    if (index >= 0 && day >= 1 && day <= 31) {
      const year = named.year ? Number(named.year) : today.getFullYear();
      const date = new Date(year, index, day);
      // A bare "Sep 15" already past rolls to next year, which is what
      // somebody setting a DUE date means by it.
      if (!named.year && date.getTime() < today.getTime()) date.setFullYear(year + 1);
      return isoDay(date);
    }
  }

  return null;
}

function shift(from: Date, days: number): Date {
  return new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
}

/** How a due date leans against today: past, inside the week, or further
 *  out. Drives the calendar glyph's colour, nothing about the words. */
export type DueTone = "overdue" | "soon" | "later";

const WEEKDAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * The due date the way Linear writes it (user-directed, 2026-09-20).
 *
 * Inside the week the value is a WORD — `Today`, `Tomorrow`, then the weekday
 * — because a date that close is read as "when do I have to move", not as a
 * calendar position. From a week out, and once it has passed, it is the
 * numeric date. The tone is computed against today rather than stored, so a
 * record nobody has touched still turns red on the morning it is late.
 *
 * `hint` is the tooltip: the numeric date behind a word, and how many days
 * late an overdue one is — the figure the old `Jul 1 · 70d late` value used
 * to carry inline, kept where a hover can still find it.
 */
export function dueLabel(iso: string, today: Date): { text: string; tone: DueTone; hint: string } {
  const date = parseDay(iso);
  if (!date) return { text: iso, tone: "later", hint: iso };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((date.getTime() - start.getTime()) / 86_400_000);
  const numeric = `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;
  if (days < 0) return { text: numeric, tone: "overdue", hint: `${numeric} · ${-days}d late` };
  if (days === 0) return { text: "Today", tone: "soon", hint: numeric };
  if (days === 1) return { text: "Tomorrow", tone: "soon", hint: numeric };
  if (days < 7) return { text: WEEKDAYS_LONG[date.getDay()]!, tone: "soon", hint: numeric };
  return { text: numeric, tone: "later", hint: numeric };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

// ---------------------------------------------------------------------------
// The value control
// ---------------------------------------------------------------------------

/** The resting look of every value: no border, no ground, 8px of padding
 *  pulled back out so the text still lines up with the column (13). */
function valueShell(open: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minHeight: 28,
    padding: "3px 8px",
    marginLeft: -8,
    borderRadius: 7,
    border: "none",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    textAlign: "left",
    maxWidth: "100%",
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    // Resting ground is the stylesheet's (`.property-row` hover) — an inline
    // `transparent` here would outrank it.
    background: open ? C.propOpen : undefined,
    color: C.propValue,
    cursor: "pointer",
  };
}

/** A shared compact ground for a property's icon, value and optional clear. */
function propertyPillShell(open: boolean): React.CSSProperties {
  return {
    ...valueShell(open), gridColumn: "1 / -1", justifySelf: "start",
    width: "fit-content", borderRadius: 999, gap: 10,
  };
}

type PickerKind = "choice" | "reference" | "date" | "text";

function pickerKind(field: ReviewField): PickerKind {
  if (field.choices && field.choices.length > 0) return "choice";
  if (field.reference) return "reference";
  if (field.inputType === "date") return "date";
  return "text";
}

// ---------------------------------------------------------------------------
// PropertyRows
// ---------------------------------------------------------------------------

export interface PropertyRowsProps {
  item: ConsoleRow;
  search: string;
  onPatchChange: (patch: Record<string, unknown>) => void;
  /**
   * Commit now, rather than when the debounce runs out.
   *
   * Typing is coalesced and committed on blur — that is what a debounce is
   * for. But a PICK is not typing: choosing "Completed" from a list is a
   * finished decision the moment it is clicked, and the picker portals out of
   * the card, so no `focusout` ever crosses the caller's blur handler to
   * commit it. Without this the reader watched a status change and a "Saving…"
   * line sit there for the better part of a second with nothing having left
   * the browser.
   */
  onCommit?: () => void;
  /** Fixed instant for the overdue arithmetic. Tests pass a literal. */
  now?: Date;
}

/**
 * The description and properties share a row only when the card has room
 * for two readable columns. Otherwise the properties follow the description.
 *
 * ONE component, and therefore one state, because `buildReviewPatch` builds a
 * patch from the WHOLE field set rather than from a delta. Split across two
 * components with a `useState` each, editing the description and then the
 * status would post a patch carrying the description as it was when the page
 * loaded — silently reverting what had just been typed.
 */
export function PropertyEditor({
  item,
  search,
  onPatchChange,
  onCommit,
  now,
  descriptionPlaceholder = "Add a description…",
}: PropertyRowsProps & { descriptionPlaceholder?: string }) {
  const state = usePropertyState(item, search, onPatchChange);
  const lead = state.fields.find((field) => field.multiline);
  return (
    <div
      data-testid="property-editor"
      // Respond to the card width, not the viewport: this editor is already
      // inside a column beside Photos on desktop.
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
        gap: 24,
        alignItems: "start",
        minWidth: 0,
        padding: "6px 18px 16px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
        {lead && (
          <textarea
            data-testid="property-lead"
            aria-label={lead.label}
            placeholder={descriptionPlaceholder}
            rows={2}
            value={state.values[lead.key] ?? ""}
            onInput={(event) =>
              state.update(lead, (event.target as HTMLTextAreaElement).value)
            }
            onChange={(event) => state.update(lead, event.target.value)}
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              border: "none",
              background: "transparent",
              padding: 0,
              margin: 0,
              resize: "none",
              overflow: "hidden",
              height: "auto",
              fieldSizing: "content",
              fontFamily: "inherit",
              fontSize: 16.1,
              lineHeight: 1.55,
              color: C.propValue,
              outline: "none",
            } as React.CSSProperties}
          />
        )}
      </div>
      <PropertyRows state={state} now={now} onCommit={onCommit} />
    </div>
  );
}

export function PropertyRows({
  state,
  now,
  onCommit,
}: {
  state: PropertyState;
  now?: Date;
  onCommit?: () => void;
}) {
  const today = useMemo(() => {
    const at = now ?? new Date();
    return new Date(at.getFullYear(), at.getMonth(), at.getDate());
  }, [now]);

  // The description is the page's lead text, not a property row.
  const properties = state.fields.filter((field) => !field.multiline);

  /** First letters, claimed in order — see the file header on why these are
   *  derived rather than mapped. */
  const shortcuts = useMemo(() => {
    const taken = new Set<string>();
    const byKey = new Map<string, string>();
    for (const field of properties) {
      if (field.calculated) continue;
      const letter = field.label.trim()[0]?.toUpperCase();
      if (!letter || !/[A-Z]/.test(letter) || taken.has(letter)) continue;
      taken.add(letter);
      byKey.set(field.key, letter);
    }
    return byKey;
  }, [properties]);

  const [openKey, setOpenKey] = useState<string | null>(null);

  // A shortcut opens a picker from anywhere on the page — but not while the
  // reader is typing into one, or into the description above it.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (openKey) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      const letter = event.key.toUpperCase();
      for (const [key, assigned] of shortcuts) {
        if (assigned !== letter) continue;
        event.preventDefault();
        setOpenKey(key);
        return;
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [shortcuts, openKey]);

  if (properties.length === 0) return null;

  return (
    <div data-testid="property-rows" style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, containerType: "inline-size", containerName: "properties" }}>
      <div
        style={{
          fontSize: 13.8,
          fontWeight: 600,
          color: C.propLabel,
          marginBottom: 6,
        }}
      >
        Properties
      </div>
      {properties.map((field) => (
        <PropertyRow
          key={field.key}
          field={field}
          state={state}
          today={today}
          shortcut={shortcuts.get(field.key)}
          open={openKey === field.key}
          onOpenChange={(next) => setOpenKey(next ? field.key : null)}
          onCommit={onCommit}
        />
      ))}
    </div>
  );
}

function PropertyRow({
  field,
  state,
  today,
  shortcut,
  open,
  onOpenChange,
  onCommit,
}: {
  field: ReviewField;
  state: PropertyState;
  today: Date;
  shortcut?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCommit?: () => void;
}) {
  /** A pick is a finished decision — see `onCommit`. */
  const pick = (apply: () => void) => {
    apply();
    onOpenChange(false);
    onCommit?.();
  };
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const kind = pickerKind(field);
  const value = state.values[field.key] ?? "";

  if (field.calculated) {
    return (
      <div data-testid="property-row" data-property={field.key} className="property-row">
        {/* NO `property-pill` class, deliberately. A calculated field is
            DERIVED — a task's company comes from whoever is assigned, and
            there is nothing here to pick. The class's only two jobs are the
            hover ground and revealing the clear ×, and both of them advertise
            an interaction this row does not have: the value lit up under the
            pointer exactly like the selectable rows above it, so it read as a
            control that silently did nothing when clicked. Layout is inline
            (`propertyPillShell`), so dropping the class costs no geometry. */}
        <span data-testid="property-pill" style={{ ...propertyPillShell(false), cursor: "default" }}>
          <PropertyLead field={field} value={value} today={today} />
          <output aria-label={field.label} data-testid="property-value" style={{ color: value ? C.propValue : C.propLabel, minWidth: 0, overflowWrap: "anywhere" }}>
            {value || "Not set"}
          </output>
        </span>
      </div>
    );
  }

  if (field.reference === "assignee") {
    return (
      <div
        data-testid="property-row"
        data-property={field.key}
        className="property-row"
        data-open={open || undefined}
      >
        <span className={state.item.assigneeDirectory ? "property-pill" : "property-pill assignee-name-pill"} data-testid="property-pill" style={{ ...propertyPillShell(open), padding: "0 8px" }}>
          <PropertyLead field={field} value={value} today={today} onClick={() => onOpenChange(!open)} />
          <AssigneePicker
            // The avatar is the row's lead glyph now; a second one in the chip
            // would be the same face twice.
            showAvatar={false}
            variant="property"
            jobSiteId={state.item.jobSiteId}
            visitId={state.item.visitId}
            category={itemEditCategory(state.item)}
            search={state.search}
            label={field.label}
            value={value}
            // The column's values are left-aligned; the review table's are not.
            align="start"
            valueStyle={{ ...valueShell(false), marginLeft: 0, padding: "3px 0", background: "transparent", width: "auto" }}
            wrapName
            suggestion={state.item.assigneeSuggestion}
            directoryEnabled={state.item.assigneeDirectory}
            multiple={state.item.syncSource === "procore" && (state.item.kind === "task" || state.item.kind === "punch" || state.item.category === "observations")}
            additionalAssignees={state.references.assignee?.additionalAssignees ?? state.item.additionalAssignees}
            // A pick made a moment ago is not on the record yet — see the prop.
            selectedRemoteId={state.references.assignee?.remoteId}
            // See `ReviewQueue`'s copy: an observation is a daily-log item,
            // and `kind` alone would head the sheet "Daily log".
            typeLabel={mobileRecordKindLabel(state.item) ?? KIND_LABEL[state.item.kind]}
            testId="property-value"
            forceOpen={open}
            onOpenChange={onOpenChange}
            onChange={(next, reference) => {
              state.update(field, next, reference);
              onCommit?.();
            }}
          />
        </span>
      </div>
    );
  }

  if (kind === "date") {
    const due = value ? dueLabel(value, today) : null;
    const openPicker = () => onOpenChange(!open);
    return (
      <div
        data-testid="property-row"
        data-property={field.key}
        className="property-row"
        data-open={open || undefined}
      >
        {/* Linear's date pill: glyph, value and the × that clears it share one
            ground, which shows on hover and stays while the picker is open.
            It spans the row's glyph column so the calendar still lines up
            with the other rows' leads. The × is a sibling of the trigger, not
            a child — a button inside a button is invalid HTML. */}
        <span
          data-testid="property-pill"
          className="property-pill"
          style={{
            ...propertyPillShell(open),
            padding: value ? "0 6px 0 8px" : "0 8px",
          }}
        >
          <PropertyLead field={field} value={value} today={today} onClick={openPicker} />
          <button
            ref={anchorRef}
            type="button"
            data-testid="property-value"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={openPicker}
            style={{ ...valueShell(false), marginLeft: 0, padding: "3px 0", background: "transparent" }}
            title={[due?.hint ?? field.label, shortcut ? `press ${shortcut}` : undefined].filter(Boolean).join(" — ")}
          >
            <PropertyValue field={field} value={value} state={state} today={today} />
          </button>
          {value && (
            <button
              type="button"
              className="property-clear"
              data-testid="property-clear"
              aria-label={`Clear ${field.label.toLowerCase()}`}
              title={`Clear ${field.label.toLowerCase()}`}
              onClick={() => pick(() => state.update(field, ""))}
              style={{
                display: "grid",
                placeItems: "center",
                width: 18,
                height: 18,
                flex: "none",
                padding: 0,
                border: "none",
                borderRadius: 999,
                background: C.propClear,
                color: C.surface,
                cursor: "pointer",
              }}
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </span>
        {open && (
          <AnchoredPanel
            anchorRef={anchorRef}
            role="listbox"
            label={field.label}
            testId="property-picker"
            minWidth={250}
            maxWidth={300}
            panelStyle={PICKER_STYLE}
            onClose={() => onOpenChange(false)}
          >
            <DatePicker
              value={value}
              today={today}
              shortcut={shortcut}
              onPick={(next) => pick(() => state.update(field, next))}
            />
          </AnchoredPanel>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="property-row"
      data-property={field.key}
      className="property-row"
    >
      {kind === "text" ? (
        <label className="property-pill" data-testid="property-pill" style={{ ...propertyPillShell(false), cursor: "text" }}>
          <PropertyLead field={field} value={value} today={today} />
          <input
            data-testid="property-value"
            aria-label={field.label}
            placeholder={`Add ${field.label.toLowerCase()}`}
            value={value}
            onInput={(event) =>
              state.update(field, (event.target as HTMLInputElement).value)
            }
            onChange={(event) => state.update(field, event.target.value)}
            style={{
              ...valueShell(false),
              outline: "none",
              cursor: "text", width: "auto", minWidth: 0, maxWidth: "100%",
              fieldSizing: "content", padding: 0, marginLeft: 0, background: "transparent",
            }}
          />
        </label>
      ) : kind === "choice" || kind === "reference" ? (
        <>
          <span className="sr-only">{field.label}</span>
          <FieldCombobox
            showIndicator={false}
            label={field.label}
            value={kind === "choice" ? value : state.references[field.reference!]?.remoteId ?? state.directoryFor(field.reference).find(option => option.name === value)?.remoteId ?? ""}
            options={kind === "choice"
              ? field.choices!.map(choice => ({ value: choice, label: choiceLabel(choice), icon: optionGlyphFor(field, choice) }))
              : state.directoryFor(field.reference).map(option => ({ value: option.remoteId, label: option.name, detail: option.path ?? option.company ?? option.email,
                  icon: referenceGlyphFor(field.reference, option.name) }))}
            open={open} onOpenChange={onOpenChange}
            onRequestOptions={kind === "reference" ? state.loadOptions : undefined}
            loading={kind === "reference" && state.optionsLoading}
            error={kind === "reference" && state.optionsFailed ? "The project directory could not be loaded." : undefined}
            emptyText="This project has no directory entries yet."
            clearLabel={kind === "reference" && value ? "Clear" : undefined}
            onCreate={kind === "reference" ? next => pick(() => state.update(field, next)) : undefined}
            onChange={next => {
              if (kind === "choice") { if (next !== null) pick(() => state.update(field, next)); return; }
              const reference = state.directoryFor(field.reference).find(option => option.remoteId === next);
              pick(() => state.update(field, reference?.name ?? "", reference));
            }}
            triggerTestId="property-value" panelTestId="property-picker" inputTestId="picker-filter" optionTestId="picker-option"
            align="start" triggerStyle={{ ...propertyPillShell(open), justifyContent: "flex-start" }}
            title={[value || field.label, shortcut ? `press ${shortcut}` : undefined].filter(Boolean).join(" — ")}
          >
            <PropertyLead field={field} value={value} today={today} hideLabel />
            <PropertyValue field={field} value={value} state={state} today={today} />
          </FieldCombobox>
        </>
      ) : (
        <button
          ref={anchorRef}
          type="button"
          data-testid="property-value"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => {
            if (field.reference) state.loadOptions();
            onOpenChange(!open);
          }}
          style={propertyPillShell(open)}
          title={[value || field.label, shortcut ? `press ${shortcut}` : undefined].filter(Boolean).join(" — ")}
        >
          <PropertyLead field={field} value={value} today={today} hideLabel />
          <PropertyValue field={field} value={value} state={state} today={today} />
        </button>
      )}
    </div>
  );
}

/** What a value looks like at rest: the word. The glyph that used to sit
 *  beside it — dot, calendar, pin, avatar — leads the row now (`PropertyLead`),
 *  the way Linear's panel reads — and for a date the glyph carries the mood
 *  too (`CalendarGlyph`), so the word itself stays in the value ink. */
function PropertyValue({
  field,
  value,
  state,
  today,
}: {
  field: ReviewField;
  value: string;
  state: PropertyState;
  today: Date;
}) {
  if (!value) {
    return (
      <span style={{ color: C.propEmpty }}>{`Add ${field.label.toLowerCase()}`}</span>
    );
  }
  if (field.choices) {
    return <>{choiceLabel(value)}</>;
  }
  if (field.inputType === "date") {
    return <span style={{ whiteSpace: "nowrap" }}>{dueLabel(value, today).text}</span>;
  }
  void state;
  return <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{value}</span>;
}

/** The design's 20px avatar. Initials, never a photo: the console has no
 *  avatar images and a coloured circle with two letters is what the directory
 *  can actually answer. */
function Initials({ name }: { name: string }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join("");
  // The letters are painted by `.initials::before` from the attribute, not
  // written as text: an avatar is decoration, and its two letters must not
  // read as part of the row's words (to a screen reader, or to a test that
  // asks a row for its label).
  return (
    <span
      aria-hidden
      className="initials"
      data-initials={letters || "—"}
      style={{
        width: 20,
        height: 20,
        borderRadius: 10,
        flex: "none",
        display: "grid",
        placeItems: "center",
        background: C.ink,
        color: C.surface,
        fontSize: 10.35,
        fontWeight: 700,
        letterSpacing: "0.2px",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// The date picker
// ---------------------------------------------------------------------------

/** 13d — a natural-language line over a month grid, today outlined. */
function DatePicker({
  value,
  today,
  shortcut,
  onPick,
}: {
  value: string;
  today: Date;
  shortcut?: string;
  onPick: (value: string) => void;
}) {
  const selected = parseDay(value);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  useFilterFocus(inputRef);

  const typed = parseDateInput(query, today);

  return (
    <>
      <PickerHeader
        placeholder="Sep 15, next Mon, +2w…"
        shortcut={shortcut}
        query={query}
        onQuery={setQuery}
        inputRef={inputRef}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          if (typed) onPick(typed);
        }}
      />
      <Calendar
        value={value}
        today={today}
        footer={false}
        testId="property-calendar"
        onSelect={onPick}
      />
      {value && (
        <div style={{ marginTop: 4 }}>
          <PickerOption label="Clear" checked={false} onSelect={() => onPick("")} />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared state
// ---------------------------------------------------------------------------

export interface PropertyState {
  item: ConsoleRow;
  search: string;
  fields: ReviewField[];
  values: Record<string, string>;
  /** The directory picks made in this editing session, by reference kind.
   *  Read by the assignee row, which has to know its own new selection is a
   *  selection — see `AssigneePicker`'s `selectedRemoteId`. */
  references: Partial<
    Record<"location" | "assignee" | "trade" | "company", ItemEditDirectoryOption | null>
  >;
  update: (field: ReviewField, value: string, reference?: ItemEditDirectoryOption) => void;
  loadOptions: () => void;
  directoryFor: (reference: ReviewField["reference"]) => ItemEditDirectoryOption[];
  optionsLoading: boolean;
  optionsFailed: boolean;
  /** The backend's own sentence for a directory that did not load, when a
   *  host has it — the only thing that says whether retrying is worth it. */
  optionsError?: string;
  /** The project's own observation types, once the directory has loaded. */
  observationTypes: string[];
}

/**
 * The values, the references and the directory — the same three pieces
 * `FieldRows` keeps, and kept the same way.
 *
 * Every change reports the WHOLE patch, because `buildReviewPatch` builds one
 * from the current values rather than from a delta. The caller debounces and
 * flushes on blur; this component never posts.
 */
export function usePropertyState(
  item: ConsoleRow,
  search: string,
  onPatchChange: (patch: Record<string, unknown>) => void,
  /** Adds the properties a surface edits beyond `fieldsFor`'s shared set —
   *  the record page's punch Status and task Category. They join the same
   *  value map, so the whole-record patch still carries every field. */
  extendFields?: (fields: ReviewField[], item: ConsoleRow) => ReviewField[],
): PropertyState {
  const fields = useMemo(
    () => (extendFields ? extendFields(fieldsFor(item), item) : fieldsFor(item)),
    // `extendFields` is a module-level function at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item],
  );
  const mobileDefinition = useMemo(() => mobileRecordDefinition(item), [item]);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.key, field.rawValue])),
  );
  const [references, setReferences] = useState<
    Partial<Record<"location" | "assignee" | "trade" | "company", ItemEditDirectoryOption | null>>
  >({});
  const incomingValues = Object.fromEntries(fields.map(field => [field.key, field.rawValue]));
  const previousValues = useRef(incomingValues);
  useEffect(() => {
    const previous = previousValues.current;
    previousValues.current = incomingValues;
    if (item.kind !== "punch") return;
    // Keep unacknowledged local edits while adopting other devices' changes.
    // Once a directory choice is echoed back, the record owns its identity.
    const settled = fields.filter(field => values[field.key] === previous[field.key] || values[field.key] === incomingValues[field.key]);
    setValues(current => Object.fromEntries(Object.entries(incomingValues).map(([key, value]) =>
      [key, current[key] === previous[key] ? value : current[key] ?? value])));
    setReferences(current => {
      const next = { ...current };
      for (const field of settled) if (field.reference) delete next[field.reference];
      return next;
    });
  }, [JSON.stringify(incomingValues)]);
  const [options, setOptions] = useState<ItemEditOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const requested = useRef(false);

  useEffect(() => {
    setValues(Object.fromEntries(fields.map((field) => [field.key, field.rawValue])));
    setReferences({});
    setOptions(null);
    setOptionsLoading(false);
    setOptionsFailed(false);
    requested.current = false;
  }, [item.id, item.jobSiteId, item.category, search]);

  const loadOptions = () => {
    if (requested.current) return;
    requested.current = true;
    setOptionsLoading(true);
    // Share the review editor's directory context, including task categories
    // inherited from the source note.
    void fetchItemEditOptions(item.jobSiteId, itemEditCategory(item), search, item.visitId)
      .then((next) => {
        setOptions(next);
        setOptionsFailed(false);
      })
      .catch(() => {
        setOptions(null);
        setOptionsFailed(true);
      })
      .finally(() => setOptionsLoading(false));
  };

  const update = (
    field: ReviewField,
    value: string,
    reference?: ItemEditDirectoryOption,
  ) => {
    const { values: nextValues, references: nextReferences } = updateDirectoryFields(
      fields, field, value, reference, values, references, options,
    );
    setValues(nextValues);
    setReferences(nextReferences);
    onPatchChange(
      buildReviewPatch(item, fields, nextValues, nextReferences, mobileDefinition?.fields),
    );
  };

  const directoryFor = (reference: ReviewField["reference"]) => {
    if (!options) return [];
    if (reference === "location") return options.locations;
    if (reference === "assignee") return options.assignees;
    if (reference === "trade") return options.trades;
    if (reference === "company") return options.companies;
    return [];
  };

  return {
    item,
    search,
    fields,
    values,
    references,
    update,
    loadOptions,
    directoryFor,
    optionsLoading,
    optionsFailed,
    observationTypes: options?.observationTypes ?? [],
  };
}
